import { motion } from 'framer-motion';
import { Shield, Award, Users, Target } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGES } from '../seo/meta.mjs';

const About = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <Seo {...PAGES.about} />
      <section
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1761213230327-d89a8cc9cc63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxlbmdpbmVlciUyMGxvb2tpbmclMjBhdCUyMGJsdWVwcmludHMlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzcwMjkyNTg0fDA&ixlib=rb-4.1.0&q=85)'
        }}
        data-testid="about-hero-section"
      >
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6"
            data-testid="about-hero-title"
          >
            About <span className="text-ocean-500">Ytek Controls</span>
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto"
          >
            Engineering excellence and reliability in every hydro power control system
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-slate-950" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-8">
              Our Vision
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-400 mb-6">
              To be the most trusted engineering partner for hydro power control systems in India and
              beyond, delivering solutions that combine technical precision with operational excellence.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-slate-500">
              We believe that reliable power infrastructure begins with well-engineered control systems.
              Every project we undertake reflects our commitment to this principle.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Engineering-First Approach
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              data-testid="value-card-technical"
              className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
            >
              <Target className="text-ocean-500 mb-4" size={36} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Technical Precision
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Every design decision backed by engineering analysis and industry standards
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              data-testid="value-card-quality"
              className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
            >
              <Shield className="text-ocean-500 mb-4" size={36} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">Quality Focus</h3>
              <p className="text-slate-400 leading-relaxed">
                Rigorous testing protocols ensure reliability in the most demanding conditions
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-testid="value-card-standards"
              className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
            >
              <Award className="text-ocean-500 mb-4" size={36} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Standards Compliance
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Full adherence to IEC, IS, and utility-specific requirements
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              data-testid="value-card-partnership"
              className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
            >
              <Users className="text-ocean-500 mb-4" size={36} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Client Partnership
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Long-term relationships built on trust, transparency, and technical competence
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-950" data-testid="manufacturing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-6">
                Manufacturing Excellence
              </h2>
              <p className="text-lg text-slate-400 mb-6 leading-relaxed">
                Our manufacturing facility combines modern equipment with skilled craftsmanship. Every
                control panel is built to exacting specifications and undergoes comprehensive testing
                before shipment.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-ocean-500 mt-1">•</span>
                  In-house panel fabrication and wiring
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-ocean-500 mt-1">•</span>
                  Factory Acceptance Testing (FAT) protocols
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-ocean-500 mt-1">•</span>
                  Quality management systems and traceability
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="text-ocean-500 mt-1">•</span>
                  Supplier qualification and component testing
                </li>
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1759159091682-3b98f4759367?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwY29udHJvbCUyMHBhbmVsJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzAyOTI1ODN8MA&ixlib=rb-4.1.0&q=85"
                alt="Manufacturing facility"
                className="w-full h-96 object-cover border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="why-ytek-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Why Ytek Controls
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 border border-white/5 p-8" data-testid="why-card-experience">
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                Proven Experience
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Over 15 years of specialized experience in hydro power control systems across diverse
                project scales and configurations.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-white/5 p-8" data-testid="why-card-capabilities">
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                Complete Capabilities
              </h3>
              <p className="text-slate-400 leading-relaxed">
                From initial concept and design through manufacturing, testing, installation, and
                commissioning – all under one roof.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-white/5 p-8" data-testid="why-card-support">
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                Long-term Support
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Our relationship doesn't end at commissioning. We provide ongoing technical support and
                system upgrades throughout the project lifecycle.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
